/**
 * Conversation Cache
 *
 * Manages caching of conversations list
 */

import { Conversation, ConversationFilters } from "@/types/centralizedChat";
import { logger } from "@/utils/logger";
import {
  ConversationCacheEntry,
  CACHE_TTL_MS,
  STALE_WHILE_REVALIDATE_MS,
} from "./types";

class ConversationCache {
  private cache = new Map<string, ConversationCacheEntry>();
  private inFlightRequests = new Map<string, Promise<ConversationCacheEntry>>();

  private getCacheKey(filters: ConversationFilters = {}): string {
    return JSON.stringify({
      unreadOnly: filters.unreadOnly || false,
      searchQuery: filters.searchQuery || "",
      status: filters.status?.sort() || [],
      manufacturerCompany: filters.manufacturerCompany || "",
    });
  }

  get(filters: ConversationFilters = {}): ConversationCacheEntry | null {
    const key = this.getCacheKey(filters);
    const entry = this.cache.get(key);

    if (!entry) return null;

    const age = Date.now() - entry.timestamp;

    // Return fresh cache
    if (age < CACHE_TTL_MS) {
      return entry;
    }

    // Return stale cache (will be revalidated)
    if (age < STALE_WHILE_REVALIDATE_MS) {
      return entry;
    }

    // Cache expired
    return null;
  }

  set(conversations: Conversation[], filters: ConversationFilters = {}): void {
    const key = this.getCacheKey(filters);
    this.cache.set(key, {
      data: conversations,
      filters: key,
      timestamp: Date.now(),
    });

    logger.debug("Conversation cache updated", {
      component: "ConversationCache",
      cacheKey: key,
      conversationCount: conversations.length,
    });
  }

  invalidate(filters?: ConversationFilters): void {
    if (filters) {
      const key = this.getCacheKey(filters);
      this.cache.delete(key);

      logger.debug("Invalidated conversation cache", {
        component: "ConversationCache",
        cacheKey: key,
      });
    } else {
      this.cache.clear();
      logger.debug("Cleared all conversation cache", {
        component: "ConversationCache",
      });
    }
  }

  trackInFlight(key: string, promise: Promise<ConversationCacheEntry>): void {
    this.inFlightRequests.set(key, promise);
    promise.finally(() => {
      this.inFlightRequests.delete(key);
    });
  }

  getInFlight(key: string): Promise<ConversationCacheEntry> | undefined {
    return this.inFlightRequests.get(key);
  }

  clear(): void {
    this.cache.clear();
    this.inFlightRequests.clear();
  }
}

export const conversationCache = new ConversationCache();
